import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, X, ImagePlus, Upload, Link, MapPin, DollarSign, Camera, FolderOpen } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { CATEGORIES, ALL_LOCATIONS } from "@/lib/constants";
import api from "@/lib/api";

export default function CreateListing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [uploadMode, setUploadMode] = useState("file"); // "file" or "url"
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const locationInputRef = useRef(null);
  const [locationQuery, setLocationQuery] = useState("");
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", category: "", images: [],
    price_per_day: "", location: user?.location || "", location_type: "us",
    delivery_option: "pickup", delivery_location: "", condition: "Good",
    for_sale: false, sale_price: "",
  });

  // Handle click outside for location dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (locationInputRef.current && !locationInputRef.current.contains(e.target)) {
        setShowLocationSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Initialize locationQuery if user has a location
  useEffect(() => {
    if (form.location) {
      setLocationQuery(form.location);
    }
  }, []);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <h2 className="font-heading font-semibold text-xl mb-2">Log in to create a listing</h2>
        <p className="text-stone-500 text-sm mb-4">You need an account to lend items.</p>
        <Button onClick={() => navigate("/auth")} className="rounded-full bg-coral hover:bg-coral text-white">Log In</Button>
      </div>
    );
  }

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocationQuery(value);
    if (value.trim().length > 0) {
      const filtered = ALL_LOCATIONS.filter((loc) =>
        loc.name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 8);
      setFilteredLocations(filtered);
      setShowLocationSuggestions(true);
    } else {
      setShowLocationSuggestions(false);
      setForm({ ...form, location: "" });
    }
  };

  const selectLocation = (location) => {
    setLocationQuery(location.name);
    setForm({ ...form, location: location.name });
    setShowLocationSuggestions(false);
  };

  const addImageUrl = () => {
    if (imageUrl.trim() && form.images.length < 5) {
      setForm((f) => ({ ...f, images: [...f.images, imageUrl.trim()] }));
      setImageUrl("");
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const remainingSlots = 5 - form.images.length;
    const filesToUpload = files.slice(0, remainingSlots);
    
    if (files.length > remainingSlots) {
      toast.warning(`Only ${remainingSlots} more image(s) can be added`);
    }

    setUploading(true);
    const newImages = [];

    for (const file of filesToUpload) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await api.post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        newImages.push(res.data.url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (newImages.length > 0) {
      setForm((f) => ({ ...f, images: [...f.images, ...newImages] }));
      toast.success(`${newImages.length} image(s) uploaded`);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (i) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.location || !form.price_per_day) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (form.images.length < 1) {
      toast.error("Please add at least 1 image");
      return;
    }
    if (form.for_sale && !form.sale_price) {
      toast.error("Please enter a sale price");
      return;
    }
    setLoading(true);
    try {
      const locType = ALL_LOCATIONS.find((l) => l.name === form.location)?.type || "us";
      const payload = {
        ...form,
        price_per_day: parseFloat(form.price_per_day),
        location_type: locType,
        sale_price: form.for_sale ? parseFloat(form.sale_price) : null,
      };
      const res = await api.post("/items", payload);
      toast.success("Listing created!");
      navigate(`/items/${res.data.id}`);
    } catch (err) {
      // Task 2B — Listing eligibility guardrail: redirect lender to payout setup
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "payout_setup_required") {
        toast.error(detail.message || "Set up payouts before listing.");
        navigate(detail.action_url || "/my-profile?tab=payments");
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Failed to create listing");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="create-listing-page">
      <h1 className="font-heading font-bold text-2xl sm:text-3xl text-stone-900 mb-2">Lend an Item</h1>
      <p className="text-stone-500 text-sm mb-8">Share your items with travelers and neighbors</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title */}
        <div>
          <Label htmlFor="title">Title *</Label>
          <Input id="title" required placeholder="e.g. Premium Surfboard" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-1 h-12 rounded-xl" data-testid="listing-title-input" />
        </div>

        {/* Description */}
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" placeholder="Describe your item, its condition, and any details borrowers should know..."
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-1 rounded-xl min-h-[120px]" data-testid="listing-description-input" />
        </div>

        {/* Category + Price */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Category *</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1 h-12 rounded-xl border-stone-200" data-testid="listing-category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="price">Price per Day ($) *</Label>
            <Input id="price" type="number" min="1" step="0.01" required placeholder="25.00"
              value={form.price_per_day} onChange={(e) => setForm({ ...form, price_per_day: e.target.value })}
              className="mt-1 h-12 rounded-xl" data-testid="listing-price-input" />
          </div>
        </div>

        {/* Location + Delivery */}
        <div className="grid grid-cols-2 gap-4">
          <div className="relative" ref={locationInputRef}>
            <Label>Location *</Label>
            <div className="relative mt-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                type="text"
                placeholder="Type to search location..."
                value={locationQuery}
                onChange={handleLocationChange}
                onFocus={() => locationQuery.trim() && filteredLocations.length > 0 && setShowLocationSuggestions(true)}
                className="h-12 rounded-xl pl-10"
                data-testid="listing-location-input"
              />
            </div>
            {showLocationSuggestions && filteredLocations.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-stone-200 overflow-hidden z-50 max-h-60 overflow-y-auto">
                {filteredLocations.map((loc) => (
                  <button
                    key={loc.name}
                    type="button"
                    onClick={() => selectLocation(loc)}
                    className="w-full px-4 py-3 text-left hover:bg-stone-50 flex items-center gap-3 transition-colors"
                  >
                    <MapPin className="w-4 h-4 text-stone-400" />
                    <span className="text-sm text-stone-700">{loc.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label>Delivery Option</Label>
            <Select value={form.delivery_option} onValueChange={(v) => setForm({ ...form, delivery_option: v })}>
              <SelectTrigger className="mt-1 h-12 rounded-xl border-stone-200" data-testid="listing-delivery-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="delivery">I can deliver</SelectItem>
                <SelectItem value="pickup">Pick up from location</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-stone-400 mt-1">
              {form.delivery_option === "delivery" 
                ? "You will deliver the item to the borrower free of charge." 
                : "Borrower will pick up the item. Provide a general location below."}
            </p>
          </div>
        </div>

        {/* Pickup Location */}
        {form.delivery_option === "pickup" && (
          <div>
            <Label>Pickup Location</Label>
            <Input
              value={form.delivery_location}
              onChange={(e) => setForm({ ...form, delivery_location: e.target.value })}
              className="mt-1 h-12 rounded-xl border-stone-200"
              placeholder="e.g. Downtown Miami, George Town center"
              data-testid="listing-delivery-location"
            />
            <p className="text-xs text-stone-400 mt-1">Provide a general area. After payment, message the borrower with the exact address.</p>
          </div>
        )}

        {/* Condition */}
        <div>
          <Label>Condition</Label>
          <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
            <SelectTrigger className="mt-1 h-12 rounded-xl border-stone-200" data-testid="listing-condition-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Excellent">Excellent</SelectItem>
              <SelectItem value="Good">Good</SelectItem>
              <SelectItem value="Fair">Fair</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* For Sale Option */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <div className="flex items-start gap-3">
            <Checkbox
              id="for_sale"
              checked={form.for_sale}
              onCheckedChange={(checked) => setForm({ ...form, for_sale: checked, sale_price: checked ? form.sale_price : "" })}
              className="mt-0.5"
              data-testid="for-sale-checkbox"
            />
            <div className="flex-1">
              <Label htmlFor="for_sale" className="text-sm font-medium text-stone-900 cursor-pointer">
                Also list this item for sale
              </Label>
              <p className="text-xs text-stone-500 mt-0.5">
                Allow buyers to purchase this item outright (7% deducted from your payout)
              </p>
            </div>
          </div>
          {form.for_sale && (
            <div className="pl-6">
              <Label htmlFor="sale_price" className="text-xs text-stone-600">Sale Price ($)</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="sale_price"
                  type="number"
                  min="1"
                  step="0.01"
                  placeholder="100.00"
                  value={form.sale_price}
                  onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                  className="h-10 rounded-lg pl-9"
                  data-testid="sale-price-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Images */}
        <div>
          <Label>Images * <span className="font-normal text-stone-400">(1-5 required)</span></Label>
          
          {/* Upload mode toggle */}
          <div className="flex gap-2 mt-2 mb-3">
            <button
              type="button"
              onClick={() => setUploadMode("file")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                uploadMode === "file" 
                  ? "bg-coral text-white" 
                  : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}
            >
              <Upload className="w-4 h-4" /> Upload Files
            </button>
            <button
              type="button"
              onClick={() => setUploadMode("url")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                uploadMode === "url" 
                  ? "bg-coral text-white" 
                  : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}
            >
              <Link className="w-4 h-4" /> Paste URL
            </button>
          </div>

          {/* File upload */}
          {uploadMode === "file" && (
            <div className="space-y-3">
              {/* Hidden file inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="listing-camera-input"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                data-testid="listing-file-input"
              />
              
              {/* Two button options for mobile */}
              <div className="grid grid-cols-2 gap-3">
                {/* Take Photo Button */}
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploading || form.images.length >= 5}
                  className="border-2 border-dashed border-stone-300 rounded-xl p-4 sm:p-6 text-center hover:border-coral hover:bg-coral-light/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="listing-camera-btn"
                >
                  {uploading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-coral border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : (
                    <>
                      <Camera className="w-8 h-8 mx-auto text-coral mb-2" />
                      <p className="text-sm text-stone-600 font-medium">Take Photo</p>
                      <p className="text-xs text-stone-400 mt-1 hidden sm:block">Use your camera</p>
                    </>
                  )}
                </button>

                {/* Choose from Gallery Button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || form.images.length >= 5}
                  className="border-2 border-dashed border-stone-300 rounded-xl p-4 sm:p-6 text-center hover:border-coral hover:bg-coral-light/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="listing-upload-btn"
                >
                  {uploading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-coral border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : (
                    <>
                      <FolderOpen className="w-8 h-8 mx-auto text-stone-400 mb-2" />
                      <p className="text-sm text-stone-600 font-medium">Choose Files</p>
                      <p className="text-xs text-stone-400 mt-1 hidden sm:block">From your device</p>
                    </>
                  )}
                </button>
              </div>
              
              <p className="text-xs text-stone-400 text-center">PNG, JPG up to 10MB each • {5 - form.images.length} slots remaining</p>
            </div>
          )}

          {/* URL input */}
          {uploadMode === "url" && (
            <div className="flex gap-2">
              <Input 
                placeholder="https://example.com/image.jpg" 
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)} 
                className="h-12 rounded-xl flex-1" 
                data-testid="listing-image-url-input" 
              />
              <Button 
                type="button" 
                variant="outline" 
                onClick={addImageUrl} 
                disabled={form.images.length >= 5}
                className="h-12 rounded-xl" 
                data-testid="listing-add-image-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Image previews */}
          {form.images.length > 0 && (
            <div className="flex gap-3 mt-4 flex-wrap">
              {form.images.map((img, i) => (
                <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border-2 border-stone-200 group">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)}
                    className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                    <X className="w-5 h-5 text-white" />
                  </button>
                  <span className="absolute bottom-1 right-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                    {i + 1}
                  </span>
                </div>
              ))}
            </div>
          )}
          
          <p className="text-xs text-stone-400 mt-2">{form.images.length}/5 images added</p>
        </div>

        <Button type="submit" disabled={loading}
          className="w-full h-12 rounded-full bg-coral hover:bg-coral text-white text-base btn-press" data-testid="create-listing-submit">
          {loading ? "Creating..." : "Create Listing"}
        </Button>
      </form>
    </div>
  );
}
